<script>
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { DirectUpload } from 'activestorage';

import Modal from '../../../../components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import VariablesHelp from 'dashboard/components/widgets/conversation/VariablesHelp.vue';

export default {
  name: 'AddCanned',
  components: {
    Modal,
    WootMessageEditor,
    VariablesHelp,
  },
  props: {
    responseContent: { type: String, default: '' },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      shortCode: '',
      content: this.responseContent || '',
      visibility: 'public_access',
      groupName: 'General',
      newGroupName: '',
      showNewGroupInput: false,
      showAdvanced: false,
      showVarsPanel: true,
      suggestTags: [],
      suggestTagInput: '',
      mediaAttachments: [],
      isUploadingMedia: false,
      addCanned: { showLoading: false },
      show: true,
    };
  },
  computed: {
    existingGroups() {
      return this.$store.getters.getCannedResponseGroups || ['General'];
    },
    visibilityHelpText() {
      return this.visibility === 'public_access'
        ? this.$t('CANNED_MGMT.ADD.FORM.VISIBILITY.PUBLIC_HELP')
        : this.$t('CANNED_MGMT.ADD.FORM.VISIBILITY.PRIVATE_HELP');
    },
    groupInitial() {
      return (this.groupName || 'G').charAt(0).toUpperCase();
    },
    labelOptions() {
      const labels = this.$store.getters['labels/getLabels'] || [];
      return labels
        .map(label => label.title)
        .sort((a, b) => a.localeCompare(b));
    },
    filteredSuggestTagOptions() {
      const input = (this.suggestTagInput || '').trim().toLowerCase();
      return this.labelOptions
        .filter(title => !this.suggestTags.includes(title))
        .filter(title => !input || title.toLowerCase().includes(input))
        .slice(0, 8);
    },
    isFormValid() {
      return !this.v$.content.$invalid && !this.v$.shortCode.$invalid;
    },
  },
  validations() {
    return {
      shortCode: { required, minLength: minLength(2) },
      content: {
        required: requiredIf(function contentRequired() {
          return this.mediaAttachments.length === 0;
        }),
      },
    };
  },
  mounted() {
    this.$store.dispatch('labels/get').catch(() => {});
  },
  unmounted() {
    this.cleanupLocalMediaPreviews();
  },
  methods: {
    normalizeTag(tag = '') {
      return String(tag).trim();
    },
    addSuggestTag(tag = this.suggestTagInput) {
      const nextTag = this.normalizeTag(tag);
      if (!nextTag) return;
      if (this.suggestTags.includes(nextTag)) {
        this.suggestTagInput = '';
        return;
      }
      this.suggestTags = [...this.suggestTags, nextTag];
      this.suggestTagInput = '';
    },
    removeSuggestTag(tag) {
      this.suggestTags = this.suggestTags.filter(item => item !== tag);
    },
    isSupportedMediaType(contentType = '') {
      return (
        contentType.startsWith('image/') || contentType.startsWith('video/')
      );
    },
    isImageMedia(media = {}) {
      return String(media.content_type || '').startsWith('image/');
    },
    isVideoMedia(media = {}) {
      return String(media.content_type || '').startsWith('video/');
    },
    async uploadCannedMedia(file) {
      return new Promise((resolve, reject) => {
        const upload = new DirectUpload(
          file,
          '/rails/active_storage/direct_uploads'
        );
        upload.create((error, blob) => {
          if (error) {
            reject(error);
            return;
          }
          resolve(blob);
        });
      });
    },
    extractSignedId(blob = {}) {
      return (
        blob.signed_id ||
        blob.signedId ||
        blob.attributes?.signed_id ||
        blob.attributes?.signedId ||
        blob.blob?.signed_id ||
        blob.blob?.signedId ||
        ''
      );
    },
    async onMediaFilesSelected(event) {
      const files = Array.from(event?.target?.files || []);
      if (!files.length) return;

      this.isUploadingMedia = true;
      for (const file of files) {
        if (!this.isSupportedMediaType(file.type || '')) {
          useAlert('Only image and video files are supported.');
          continue;
        }

        try {
          // eslint-disable-next-line no-await-in-loop
          const blob = await this.uploadCannedMedia(file);
          const signedId = this.extractSignedId(blob);
          if (!signedId) {
            useAlert('Upload failed: unable to get media token.');
            continue;
          }
          const previewUrl = URL.createObjectURL(file);
          this.mediaAttachments = [
            ...this.mediaAttachments,
            {
              id: blob.id || `${signedId}-${Date.now()}`,
              signed_id: signedId,
              filename: blob.filename || file.name,
              content_type: blob.content_type || file.type,
              byte_size: blob.byte_size || file.size,
              url: previewUrl,
              is_local_preview: true,
            },
          ];
        } catch (error) {
          useAlert(error?.message || 'Failed to upload media.');
        }
      }
      this.isUploadingMedia = false;

      if (event?.target) {
        event.target.value = '';
      }
    },
    removeMediaAttachment(index) {
      const item = this.mediaAttachments[index];
      if (item?.is_local_preview && item.url?.startsWith('blob:')) {
        URL.revokeObjectURL(item.url);
      }
      this.mediaAttachments = this.mediaAttachments.filter(
        (_, i) => i !== index
      );
    },
    cleanupLocalMediaPreviews() {
      this.mediaAttachments.forEach(item => {
        if (item?.is_local_preview && item.url?.startsWith('blob:')) {
          URL.revokeObjectURL(item.url);
        }
      });
    },
    resetForm() {
      this.cleanupLocalMediaPreviews();
      this.shortCode = '';
      this.content = '';
      this.visibility = 'public_access';
      this.groupName = 'General';
      this.newGroupName = '';
      this.showNewGroupInput = false;
      this.suggestTags = [];
      this.suggestTagInput = '';
      this.mediaAttachments = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    onGroupChange(e) {
      const val = e.target.value;
      if (val === '__new__') {
        this.showNewGroupInput = true;
        this.groupName = '';
      } else {
        this.showNewGroupInput = false;
        this.groupName = val;
      }
    },
    confirmNewGroup() {
      if (this.newGroupName.trim()) {
        this.groupName = this.newGroupName.trim();
        this.showNewGroupInput = false;
      }
    },
    addCannedResponse() {
      this.v$.$touch();
      if (this.v$.$invalid) return;
      this.addCanned.showLoading = true;
      this.$store
        .dispatch('createCannedResponse', {
          short_code: this.shortCode,
          content: this.content,
          visibility: this.visibility,
          group_name: this.groupName || 'General',
          suggest_tags: this.suggestTags,
          media_attachment_signed_ids: this.mediaAttachments
            .map(media => media.signed_id)
            .filter(Boolean),
        })
        .then(() => {
          this.addCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
          this.resetForm();
          this.onClose();
        })
        .catch(error => {
          this.addCanned.showLoading = false;
          useAlert(
            error?.message || this.$t('CANNED_MGMT.ADD.API.ERROR_MESSAGE')
          );
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose" size="xxl">
    <div class="cr3-dialog">
      <div class="cr3-header">
        <div class="cr3-title">Create canned response</div>
        <button
          type="button"
          class="cr3-close"
          aria-label="Close"
          @click="onClose"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="18"
            height="18"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m6 6 12 12m0-12L6 18"
            />
          </svg>
        </button>
      </div>

      <form class="cr3-body" @submit.prevent="addCannedResponse()">
        <div class="cr3-section">
          <div class="cr3-toggle">
            <button
              type="button"
              class="cr3-toggle__btn"
              :class="{ 'cr3-toggle__btn--on': visibility === 'public_access' }"
              @click="visibility = 'public_access'"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M5 7a4 4 0 1 0 8 0 4 4 0 0 0-8 0M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2M16 3.13a4 4 0 0 1 0 7.75M21 21v-2a4 4 0 0 0-3-3.85"
                />
              </svg>
              Shared
            </button>
            <button
              type="button"
              class="cr3-toggle__btn"
              :class="{
                'cr3-toggle__btn--on': visibility === 'private_access',
              }"
              @click="visibility = 'private_access'"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M8 7a4 4 0 1 0 8 0 4 4 0 0 0-8 0M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"
                />
              </svg>
              Private
            </button>
          </div>
          <p class="cr3-hint">{{ visibilityHelpText }}</p>
        </div>

        <div class="cr3-section">
          <label class="cr3-label">Response text</label>
          <div
            class="cr3-editor"
            :class="{ 'cr3-editor--err': v$.content.$error }"
          >
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
          <p v-if="v$.content.$error" class="cr3-err">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M12 8v4m0 4h.01M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0" />
            </svg>
            Enter response text or attach media.
          </p>
        </div>

        <div class="cr3-section">
          <label class="cr3-label">Shortcuts</label>
          <div
            class="cr3-shortcut"
            :class="{ 'cr3-shortcut--err': v$.shortCode.$error }"
          >
            <span class="cr3-hash">#</span>
            <input
              v-model="shortCode"
              class="cr3-shortcut__field"
              placeholder="Type shortcut..."
              @blur="v$.shortCode.$touch"
            />
          </div>
          <p class="cr3-sub">Hit enter to add a new one</p>
        </div>

        <div class="cr3-advanced">
          <button
            type="button"
            class="cr3-advanced__toggle"
            @click="showAdvanced = !showAdvanced"
          >
            <span class="cr3-advanced__text">Advanced settings</span>
            <span class="cr3-advanced__gear">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="18"
                height="18"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12.675 3a1.8 1.8 0 0 1 1.8 1.8v1.407c.462.197.9.45 1.305.753l1.218-.703a1.8 1.8 0 0 1 2.459.658l.675 1.17a1.8 1.8 0 0 1-.66 2.458l-1.217.704a6.3 6.3 0 0 1 0 1.507l1.218.703a1.8 1.8 0 0 1 .659 2.458l-.675 1.17a1.8 1.8 0 0 1-2.46.658l-1.217-.703a6.3 6.3 0 0 1-1.305.754V19.2a1.8 1.8 0 0 1-1.8 1.8h-1.35a1.8 1.8 0 0 1-1.8-1.8v-1.406a6.3 6.3 0 0 1-1.305-.754l-1.218.703a1.8 1.8 0 0 1-2.46-.658l-.674-1.17a1.8 1.8 0 0 1 .659-2.458l1.218-.704a6.3 6.3 0 0 1 0-1.506l-1.218-.704a1.8 1.8 0 0 1-.66-2.458l.676-1.17a1.8 1.8 0 0 1 2.459-.658l1.218.703a6.3 6.3 0 0 1 1.305-.753V4.8a1.8 1.8 0 0 1 1.8-1.8z"
                  clip-rule="evenodd"
                />
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M14.696 12a2.696 2.696 0 1 1-5.392 0 2.696 2.696 0 0 1 5.392 0"
                />
              </svg>
            </span>
          </button>
        </div>

        <div v-if="showAdvanced" class="cr3-advanced-panel">
          <div class="cr3-advanced-panel__section">
            <label class="cr3-label">Group selection</label>
            <div v-if="!showNewGroupInput" class="cr3-select">
              <div class="cr3-select__face">
                <span class="cr3-avatar" :style="{ background: '#6e91de' }">{{
                  groupInitial
                }}</span>
                <span class="cr3-select__text">{{
                  groupName || 'General'
                }}</span>
              </div>
              <svg
                class="cr3-select__arrow"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
              <select
                class="cr3-select__native"
                :value="groupName"
                @change="onGroupChange"
              >
                <option v-for="g in existingGroups" :key="g" :value="g">
                  {{ g }}
                </option>
                <option value="__new__">
                  + {{ $t('CANNED_MGMT.GROUP.ADD_NEW') }}
                </option>
              </select>
            </div>
            <div v-else class="cr3-inline-new">
              <input
                v-model="newGroupName"
                class="cr3-input"
                :placeholder="$t('CANNED_MGMT.GROUP.NEW_PLACEHOLDER')"
                @keyup.enter="confirmNewGroup"
              />
              <button
                type="button"
                class="cr3-btn cr3-btn--sm cr3-btn--fill"
                @click="confirmNewGroup"
              >
                OK
              </button>
              <button
                type="button"
                class="cr3-btn cr3-btn--sm"
                @click="
                  showNewGroupInput = false;
                  groupName = 'General';
                "
              >
                Cancel
              </button>
            </div>
          </div>

          <div class="cr3-advanced-panel__section">
            <label class="cr3-label">Suggest tag</label>
            <div class="cr3-tag-input">
              <input
                v-model="suggestTagInput"
                class="cr3-tag-input__field"
                placeholder="Type tag and press Enter"
                @keydown.enter.prevent="addSuggestTag()"
                @keydown.tab.prevent="addSuggestTag()"
              />
              <button
                type="button"
                class="cr3-btn cr3-btn--sm"
                @click="addSuggestTag()"
              >
                Add
              </button>
            </div>

            <div
              v-if="filteredSuggestTagOptions.length > 0"
              class="cr3-tag-suggestions"
            >
              <button
                v-for="option in filteredSuggestTagOptions"
                :key="option"
                type="button"
                class="cr3-tag-suggestion"
                @click="addSuggestTag(option)"
              >
                {{ option }}
              </button>
            </div>

            <div v-if="suggestTags.length > 0" class="cr3-tag-bound">
              <span v-for="tag in suggestTags" :key="tag" class="cr3-tag-chip">
                {{ tag }}
                <button type="button" @click="removeSuggestTag(tag)">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    width="12"
                    height="12"
                  >
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="m6 6 12 12m0-12L6 18"
                    />
                  </svg>
                </button>
              </span>
            </div>
            <p class="cr3-sub">
              Suggested tags are used when CS reply text matches this response.
            </p>
          </div>

          <div class="cr3-advanced-panel__section">
            <label class="cr3-label">Photo / video</label>
            <label class="cr3-upload-btn">
              <input
                class="cr3-upload-btn__native"
                type="file"
                accept="image/*,video/*"
                multiple
                @change="onMediaFilesSelected"
              />
              <span>{{
                isUploadingMedia ? 'Uploading...' : 'Upload photo/video'
              }}</span>
            </label>
            <p class="cr3-sub">
              Images and videos will be sent with this canned.
            </p>

            <div v-if="mediaAttachments.length > 0" class="cr3-media-grid">
              <div
                v-for="(media, index) in mediaAttachments"
                :key="`${media.signed_id}-${index}`"
                class="cr3-media-card"
              >
                <img
                  v-if="isImageMedia(media)"
                  :src="media.url"
                  alt="Canned image"
                  class="cr3-media-card__preview"
                />
                <video
                  v-else-if="isVideoMedia(media)"
                  :src="media.url"
                  class="cr3-media-card__preview"
                  controls
                  muted
                  playsinline
                />
                <div class="cr3-media-card__name">{{ media.filename }}</div>
                <button
                  type="button"
                  class="cr3-media-card__remove"
                  @click="removeMediaAttachment(index)"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>

          <div class="cr3-advanced-panel__section">
            <button
              type="button"
              class="cr3-vars-head"
              @click="showVarsPanel = !showVarsPanel"
            >
              <span class="cr3-vars-head__title">💡 Available Variables</span>
              <svg
                class="cr3-vars-head__chev"
                :class="{ 'cr3-vars-head__chev--open': showVarsPanel }"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
            <div v-show="showVarsPanel" class="cr3-vars-body">
              <VariablesHelp />
            </div>
          </div>
        </div>

        <div class="cr3-footer">
          <button type="button" class="cr3-btn" @click="onClose">Cancel</button>
          <button
            type="submit"
            class="cr3-btn cr3-btn--fill"
            :disabled="addCanned.showLoading || isUploadingMedia"
          >
            <span v-if="addCanned.showLoading" class="cr3-spin" />
            Save
          </button>
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
/* =========================================================
   Add Canned Response Dialog – LiveChat-style redesign
   ========================================================= */
.cr-dialog {
  font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    'Helvetica Neue',
    Arial,
    sans-serif;
  color: #131317;
  -webkit-font-smoothing: antialiased;
}

/* ---- header ---- */
.cr-dialog__header {
  padding: 20px 24px 16px;
  border-bottom: 1px solid #eeeeef;
}
.cr-dialog__title {
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  margin: 0;
  color: #131317;
}

/* ---- body ---- */
.cr-dialog__body {
  display: flex;
  min-height: 0;
}

/* ---- form column ---- */
.cr-form {
  flex: 1;
  min-width: 0;
  padding: 20px 24px 16px;
  display: flex;
  flex-direction: column;
}

.cr-section {
  margin-bottom: 22px;
}
.cr-section:last-of-type {
  margin-bottom: 0;
}

.cr-label {
  display: block;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  color: #131317;
  margin-bottom: 6px;
}

.cr-hint {
  font-size: 13px;
  line-height: 18px;
  color: #8d8d95;
  margin: 6px 0 0;
}

.cr-sub-hint {
  font-size: 13px;
  line-height: 20px;
  color: #8d8d95;
  margin: 4px 0 0;
}

/* ---- toggle group (shared / private) ---- */
.cr-toggle-group {
  display: inline-flex;
  border: 1px solid #e0e0e1;
  border-radius: 8px;
  padding: 3px;
  background: #fff;
}

.cr-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 18px;
  height: 30px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #62626d;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.cr-toggle-btn + .cr-toggle-btn {
  margin-left: 4px;
}
.cr-toggle-btn--on {
  background: #eeeeef;
  color: #131317;
}
.cr-toggle-btn:hover:not(.cr-toggle-btn--on) {
  background: #f5f5f5;
}

/* ---- select box (group picker) ---- */
.cr-select-box {
  position: relative;
  display: flex;
  align-items: center;
  height: 38px;
  border: 1px solid #e0e0e1;
  border-radius: 8px;
  background: #fff;
  padding: 0 12px;
  cursor: pointer;
  transition: border-color 0.15s;
}
.cr-select-box:hover {
  border-color: #c8c8ca;
}
.cr-select-box__face {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}
.cr-avatar-sm {
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  text-transform: uppercase;
  user-select: none;
}
.cr-select-box__text {
  font-size: 14px;
  color: #131317;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.cr-select-box__arrow {
  flex-shrink: 0;
  color: #8d8d95;
}
.cr-select-box__native {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  font-family: inherit;
}

/* ---- inline new group ---- */
.cr-inline-new {
  display: flex;
  gap: 8px;
  align-items: center;
}

/* ---- generic input ---- */
.cr-input {
  flex: 1;
  height: 38px;
  border: 1px solid #e0e0e1;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 14px;
  color: #131317;
  background: #fff;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s;
}
.cr-input:focus {
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.08);
}

/* ---- editor ---- */
.cr-editor {
  border: 1px solid #e0e0e1;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  transition: border-color 0.15s;
}
.cr-editor:focus-within {
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.08);
}
.cr-editor--err {
  border-color: #d92d20;
}

.cr-err-msg {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 6px 0 0;
  color: #d92d20;
  font-size: 13px;
  line-height: 20px;
}

/* ---- shortcut input ---- */
.cr-shortcut-input {
  display: flex;
  align-items: center;
  border: 1px solid #e0e0e1;
  border-radius: 8px;
  padding: 0 12px;
  height: 38px;
  background: #fff;
  transition: border-color 0.15s;
}
.cr-shortcut-input:focus-within {
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.08);
}
.cr-shortcut-input--err {
  border-color: #d92d20;
}
.cr-hash {
  color: #0066ff;
  font-weight: 600;
  font-size: 14px;
  margin-right: 4px;
  user-select: none;
}
.cr-shortcut-input__field {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #131317;
  font-family: inherit;
  line-height: 22px;
}
.cr-shortcut-input__field::placeholder {
  color: #8d8d95;
}

/* ---- footer ---- */
.cr-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 18px;
  margin-top: auto;
  border-top: 1px solid #eeeeef;
}

/* ---- buttons ---- */
.cr-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 20px;
  border: 1px solid #e0e0e1;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;
  background: #fff;
  color: #131317;
}
.cr-btn:hover {
  background: #f5f5f5;
}
.cr-btn--sm {
  height: 32px;
  padding: 0 14px;
  font-size: 13px;
}
.cr-btn--fill {
  background: #0066ff;
  color: #fff;
  border-color: #0066ff;
}
.cr-btn--fill:hover:not(:disabled) {
  background: #0059e1;
}
.cr-btn--fill:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.cr-btn--ghost {
  background: transparent;
  border-color: #e0e0e1;
}
.cr-btn--ghost:hover {
  background: #f5f5f5;
}

.cr-spin {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: cr-spin-anim 0.6s linear infinite;
}
@keyframes cr-spin-anim {
  to {
    transform: rotate(360deg);
  }
}

/* ---- variables panel ---- */
.cr-vars {
  width: 260px;
  flex-shrink: 0;
  border-left: 1px solid #eeeeef;
  display: flex;
  flex-direction: column;
  max-height: 70vh;
  background: #fafafa;
}
.cr-vars__head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid #eeeeef;
  background: #fff;
}
.cr-vars__icon {
  display: flex;
  align-items: center;
  color: #8d8d95;
}
.cr-vars__title {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: #131317;
}
.cr-vars__chevron {
  color: #8d8d95;
  transition: transform 0.25s ease;
  transform: rotate(180deg);
}
.cr-vars__chevron--open {
  transform: rotate(0deg);
}
.cr-vars__body {
  overflow-y: auto;
  padding: 12px 14px;
  flex: 1;
}

/* ---- modal chrome overrides ---- */
:deep(.modal-header) {
  display: none !important;
}
:deep(.modal-container) {
  background: #fff !important;
  border-radius: 12px !important;
  overflow: hidden;
}
:deep(.modal-body) {
  background: #fff !important;
  padding: 0 !important;
}

/* ---- editor internals ---- */
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }
  .ProseMirror-woot-style {
    @apply min-h-[7rem];
    background: #fff !important;
    border: none !important;
    border-radius: 0 !important;
    p {
      @apply text-sm;
      color: #131317 !important;
    }
  }
}

/* ---- variables-help overrides ---- */
:deep(.variables-help) {
  background: transparent !important;
}
:deep(.text-n-slate-12) {
  color: #131317 !important;
}
:deep(.text-n-slate-11) {
  color: #62626d !important;
}
:deep(.text-n-slate-10) {
  color: #8d8d95 !important;
}
:deep(.bg-n-solid-1),
:deep(.bg-n-solid-2),
:deep(.bg-n-solid-3),
:deep(.bg-n-background) {
  background: #fff !important;
}
:deep(.bg-n-alpha-1),
:deep(.bg-n-alpha-2),
:deep(.bg-n-alpha-3) {
  background: #f5f5f5 !important;
}
:deep(.border-n-weak),
:deep(.border-n-strong) {
  border-color: #eeeeef !important;
}
:deep(code),
:deep(.variable-chip),
:deep([class*='variable']) {
  background: #fff !important;
  border: 1px solid #eeeeef !important;
  color: #131317 !important;
}
/* =========================================================
   Create canned response dialog (screenshot-3 style)
   ========================================================= */
.cr3-dialog {
  font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    'Helvetica Neue',
    Arial,
    sans-serif;
  color: #131317;
  -webkit-font-smoothing: antialiased;
}

.cr3-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid #eeeeef;
}

.cr3-title {
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
}

.cr3-close {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #62626d;
  cursor: pointer;
  display: grid;
  place-items: center;
}

.cr3-close:hover {
  background: #f5f5f5;
  color: #131317;
}

.cr3-body {
  padding: 18px 22px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.cr3-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cr3-label {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.cr3-hint,
.cr3-sub {
  font-size: 13px;
  line-height: 18px;
  color: #8d8d95;
  margin: 0;
}

.cr3-toggle {
  display: inline-flex;
  border: 1px solid #e0e0e1;
  border-radius: 10px;
  padding: 4px;
  background: #fff;
  width: fit-content;
}

.cr3-toggle__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 18px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #62626d;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.cr3-toggle__btn--on {
  background: #eeeeef;
  color: #131317;
}

.cr3-editor {
  border: 1px solid #e0e0e1;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}

.cr3-editor:focus-within {
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
}

.cr3-editor--err {
  border-color: #d92d20;
}

.cr3-err {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #d92d20;
  font-size: 13px;
  line-height: 18px;
  margin: 0;
}

.cr3-shortcut {
  display: flex;
  align-items: center;
  border: 1px solid #e0e0e1;
  border-radius: 12px;
  padding: 0 12px;
  height: 40px;
  background: #fff;
}

.cr3-shortcut:focus-within {
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
}

.cr3-shortcut--err {
  border-color: #d92d20;
}

.cr3-hash {
  color: #0066ff;
  font-weight: 800;
  margin-right: 4px;
  user-select: none;
}

.cr3-shortcut__field {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #131317;
  font-family: inherit;
}

.cr3-advanced {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-top: 6px;
}

.cr3-advanced__toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: transparent;
  color: #131317;
  cursor: pointer;
  font-family: inherit;
  padding: 6px 0;
}

.cr3-advanced__text {
  font-weight: 700;
  text-decoration: underline;
}

.cr3-advanced-panel {
  border: 1px solid #eeeeef;
  border-radius: 12px;
  background: #fafafa;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.cr3-select {
  position: relative;
  display: flex;
  align-items: center;
  height: 40px;
  border: 1px solid #e0e0e1;
  border-radius: 12px;
  background: #fff;
  padding: 0 12px;
}

.cr3-select__face {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.cr3-avatar {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  color: #fff;
  font-weight: 800;
  user-select: none;
}

.cr3-select__text {
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cr3-select__arrow {
  color: #8d8d95;
}

.cr3-select__native {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  font-family: inherit;
}

.cr3-inline-new {
  display: flex;
  gap: 8px;
  align-items: center;
}

.cr3-input {
  flex: 1;
  height: 40px;
  border: 1px solid #e0e0e1;
  border-radius: 12px;
  padding: 0 12px;
  font-size: 14px;
  outline: none;
  background: #fff;
  font-family: inherit;
}

.cr3-input:focus {
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
}

.cr3-tag-input {
  display: flex;
  gap: 8px;
  align-items: center;
}

.cr3-tag-input__field {
  flex: 1;
  height: 40px;
  border: 1px solid #e0e0e1;
  border-radius: 12px;
  padding: 0 12px;
  font-size: 14px;
  outline: none;
  background: #fff;
  font-family: inherit;
}

.cr3-tag-input__field:focus {
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
}

.cr3-tag-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.cr3-tag-suggestion {
  border: 1px solid #d6e4ff;
  background: #f3f7ff;
  color: #1d4ed8;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 12px;
  line-height: 20px;
  cursor: pointer;
}

.cr3-tag-bound {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.cr3-tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  padding: 2px 10px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 12px;
  line-height: 20px;
  font-weight: 700;
}

.cr3-tag-chip button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  padding: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.cr3-media-grid {
  margin-top: 10px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
}

.cr3-media-card {
  border: 1px solid #e0e0e1;
  border-radius: 12px;
  padding: 8px;
  background: #fff;
}

.cr3-media-card__preview {
  width: 100%;
  height: 92px;
  object-fit: cover;
  border-radius: 8px;
  background: #f5f5f5;
}

.cr3-media-card__name {
  margin-top: 6px;
  font-size: 12px;
  line-height: 18px;
  color: #4b5563;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cr3-media-card__remove {
  margin-top: 6px;
  border: none;
  background: transparent;
  color: #b91c1c;
  font-size: 12px;
  line-height: 18px;
  cursor: pointer;
  padding: 0;
}

.cr3-upload-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid #e0e0e1;
  background: #fff;
  color: #131317;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.cr3-upload-btn__native {
  display: none;
}

.cr3-vars-head {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #e0e0e1;
  border-radius: 12px;
  padding: 10px 12px;
  background: #fff;
  cursor: pointer;
  font-family: inherit;
}

.cr3-vars-head__title {
  font-weight: 800;
  font-size: 14px;
}

.cr3-vars-head__chev {
  color: #8d8d95;
  transition: transform 0.2s ease;
  transform: rotate(180deg);
}

.cr3-vars-head__chev--open {
  transform: rotate(0deg);
}

.cr3-vars-body {
  margin-top: 10px;
  max-height: 260px;
  overflow: auto;
  background: #fff;
  border: 1px solid #e0e0e1;
  border-radius: 12px;
  padding: 10px 12px;
}

.cr3-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 14px;
  border-top: 1px solid #eeeeef;
}

.cr3-btn {
  height: 40px;
  padding: 0 18px;
  border-radius: 12px;
  border: 1px solid #e0e0e1;
  background: #fff;
  color: #131317;
  font-weight: 800;
  cursor: pointer;
  font-family: inherit;
}

.cr3-btn--sm {
  height: 34px;
  padding: 0 14px;
  font-weight: 700;
}

.cr3-btn--fill {
  background: #0066ff;
  border-color: #0066ff;
  color: #fff;
}

.cr3-btn--fill:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.cr3-spin {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: cr3-spin 0.6s linear infinite;
  margin-right: 6px;
}

@keyframes cr3-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
